import { Button, Flex, Text } from '@chakra-ui/react'
import React, { useEffect, useMemo, useState } from 'react'

import { Modal, SearchableCombobox } from '@mangovoice/mango-ui'
import type { SearchableComboboxOption, ToastLike } from '@mangovoice/mango-ui'
import type { PbxLocation } from '@mangovoice/types'

import type { PbxLocationsApi } from '../api/pbxLocationsApi.types'
import {
  isDeletePbxLocationReassignmentRequiredError,
  useDeletePbxLocationMutation,
  useGetPbxLocationsQuery,
} from '../queries'
import { text as tfallback } from '../types'
import type { PbxLocationsTranslateFn } from '../types'
import { composeApiErrorMessage } from '../utils/pbxLocations.utils'

interface LocationDeleteConfirmModalProps {
  api: PbxLocationsApi
  isOpen: boolean
  onClose: () => void
  /** The location being deleted. */
  location: PbxLocation
  onDeleted: () => void
  toast: ToastLike
  t?: PbxLocationsTranslateFn
}

export const LocationDeleteConfirmModal: React.FC<
  LocationDeleteConfirmModalProps
> = ({ api, isOpen, onClose, location, onDeleted, toast, t }) => {
  // Fetch the full set of locations on this PBX for reassignment. We can't
  // reuse the page's list query because it's filtered by the user's current
  // search text, which would hide valid reassignment targets.
  const { data: allLocationsData } = useGetPbxLocationsQuery({
    api,
    pbxId: location.pbx_id,
    ordering: 'name',
    options: {
      enabled: isOpen,
    },
  })
  const otherLocations = useMemo<PbxLocation[]>(() => {
    const all = allLocationsData?.results ?? []
    return all.filter((other) => other.id !== location.id)
  }, [allLocationsData, location.id])

  const [requiresReassignment, setRequiresReassignment] = useState(false)
  const [extensionCount, setExtensionCount] = useState<number>(
    location.extension_count,
  )
  const [destinationLocationId, setDestinationLocationId] = useState<
    number | null
  >(null)

  useEffect(() => {
    if (!isOpen) return
    setRequiresReassignment(false)
    setExtensionCount(location.extension_count)
    setDestinationLocationId(null)
  }, [isOpen, location.id, location.extension_count])

  const deleteMutation = useDeletePbxLocationMutation(api, {
    onSuccess: () => {
      toast.create({
        title: tfallback(
          t,
          'pbxLocations.deleteModal.success',
          'Location deleted.',
        ),
        type: 'success',
      })
      onDeleted()
      onClose()
    },
    onError: (error) => {
      if (isDeletePbxLocationReassignmentRequiredError(error)) {
        setRequiresReassignment(true)
        setExtensionCount(error.extension_count)
        return
      }
      toast.create({
        title: composeApiErrorMessage(
          error,
          tfallback(
            t,
            'pbxLocations.deleteModal.error',
            'Failed to delete location.',
          ),
        ),
        type: 'error',
      })
    },
  })

  const comboboxOptions = useMemo<SearchableComboboxOption[]>(
    () =>
      otherLocations.map((other) => ({
        id: String(other.id),
        label: other.name,
      })),
    [otherLocations],
  )

  const handleConfirm = () => {
    deleteMutation.mutate({
      id: location.id,
      newLocationId: undefined,
    })
  }

  const handleConfirmReassign = () => {
    if (destinationLocationId === null) return
    deleteMutation.mutate({
      id: location.id,
      newLocationId: destinationLocationId,
    })
  }

  const isPending = deleteMutation.isPending
  const reassignAvailable = otherLocations.length > 0

  const reassignPromptKey =
    extensionCount === 1
      ? 'pbxLocations.deleteModal.reassignPrompt_one'
      : 'pbxLocations.deleteModal.reassignPrompt_other'
  const reassignPromptFallback =
    extensionCount === 1
      ? `${extensionCount} extension is assigned to this location and must be reassigned before it can be deleted. Choose a new location:`
      : `${extensionCount} extensions are assigned to this location and must be reassigned before it can be deleted. Choose a new location:`

  const footer = requiresReassignment ? (
    <Flex gap={3} justifyContent="flex-end" width="100%">
      <Button
        variant="outline"
        colorPalette="neutral"
        rounded="lg"
        onClick={onClose}
        disabled={isPending}
      >
        {tfallback(t, 'pbxLocations.deleteModal.cancel', 'Cancel')}
      </Button>
      <Button
        colorPalette="error"
        rounded="lg"
        onClick={handleConfirmReassign}
        loading={isPending}
        disabled={!reassignAvailable || destinationLocationId === null}
      >
        {tfallback(
          t,
          'pbxLocations.deleteModal.confirmReassign',
          'Reassign and Delete',
        )}
      </Button>
    </Flex>
  ) : (
    <Flex mt={4} gap={3} justifyContent="flex-end" width="100%">
      <Button
        variant="outline"
        colorPalette="neutral"
        rounded="lg"
        onClick={onClose}
        disabled={isPending}
      >
        {tfallback(t, 'pbxLocations.deleteModal.cancel', 'Cancel')}
      </Button>
      <Button
        colorPalette="error"
        rounded="lg"
        onClick={handleConfirm}
        loading={isPending}
      >
        {tfallback(t, 'pbxLocations.deleteModal.confirm', 'Delete')}
      </Button>
    </Flex>
  )

  return (
    <Modal
      open={isOpen}
      onOpenChange={(d) => {
        if (!d.open) onClose()
      }}
      title={tfallback(t, 'pbxLocations.deleteModal.header', 'Delete Location')}
      role="alertdialog"
      maxWidth="480px"
      footer={footer}
    >
      <Flex flexDirection="column" gap="1rem">
        {!requiresReassignment ? (
          <Text>
            {tfallback(
              t,
              'pbxLocations.deleteModal.confirmMessage',
              `Are you sure you want to delete "${location.name}"? This cannot be undone.`,
              { name: location.name },
            )}
          </Text>
        ) : (
          <>
            <Text
              padding="0.75rem"
              borderRadius="8px"
              backgroundColor="red.subtle"
              border="1px solid"
              borderColor="red.emphasized"
              fontSize="sm"
            >
              {tfallback(t, reassignPromptKey, reassignPromptFallback, {
                count: extensionCount,
              })}
            </Text>
            {reassignAvailable ? (
              <SearchableCombobox
                options={comboboxOptions}
                value={
                  destinationLocationId !== null
                    ? String(destinationLocationId)
                    : ''
                }
                onChange={(value) =>
                  setDestinationLocationId(value === '' ? null : Number(value))
                }
                placeholder={tfallback(
                  t,
                  'pbxLocations.deleteModal.reassignSelectPlaceholder',
                  'Select a destination location',
                )}
                width="100%"
              />
            ) : (
              <Text color="red.fg" fontSize="sm">
                {tfallback(
                  t,
                  'pbxLocations.deleteModal.noOtherLocations',
                  'There are no other locations on this PBX. Create another location first.',
                )}
              </Text>
            )}
          </>
        )}
      </Flex>
    </Modal>
  )
}
